import React, { useMemo, useState } from "react";
import { MaterialSearchPicker } from "../MaterialSearchPicker";
import { CPInput } from "../CPInput";
import { usePermissions } from "../../hooks/usePermissions";
import {
  addWorkTypeMaterial,
  CATALOG_MATERIAL_TYPE,
  CATALOG_OTHER_PRODUCT,
  filterCatalogByMaterialType,
  updateWorkTypeMaterial,
} from "../../helpers/estimates";
import { EstimateBottomSheet } from "./EstimateBottomSheet";

function emptyOtherProductForm() {
  return {
    productName: "",
    unitCost: "",
    sellPrice: "",
    unitType: "",
    quantity: "",
  };
}

/**
 * Focused Add/Edit Material entry surface — same catalog/Other Product path,
 * quantity, and notes fields as the previous inline panel, just presented as
 * a focused sheet instead of an always-expanded form. Reuses
 * addWorkTypeMaterial/updateWorkTypeMaterial unchanged; no new persistence,
 * no calculation changes. Material quantity remains independent of measured
 * sqft.
 */
export function MaterialFormSheet({
  estimateFeatureId,
  materialsCatalog = [],
  row = null,
  onClose,
  onSaved,
}) {
  const { actor } = usePermissions();
  const productsCatalog = useMemo(
    () => filterCatalogByMaterialType(materialsCatalog, CATALOG_MATERIAL_TYPE.PRODUCT),
    [materialsCatalog],
  );

  const [materialId, setMaterialId] = useState("");
  const [quantity, setQuantity] = useState(
    row?.quantity != null ? String(row.quantity) : "",
  );
  const [notes, setNotes] = useState(row?.notes || "");
  const [otherProductForm, setOtherProductForm] = useState(emptyOtherProductForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isOtherProduct = materialId === CATALOG_OTHER_PRODUCT;
  const selectedMaterial = productsCatalog.find(
    (item) => item.material_id === materialId,
  );
  const isEditing = Boolean(row);

  async function handleSave() {
    setError("");

    if (isEditing) {
      setSaving(true);
      try {
        await updateWorkTypeMaterial(actor, row.room_material_id, {
          quantity,
          notes,
        });
        await onSaved?.();
        onClose?.();
      } catch (err) {
        setError(err.message || "Unable to update material.");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!isOtherProduct && !selectedMaterial) {
      setError("Select a product.");
      return;
    }

    setSaving(true);
    try {
      if (isOtherProduct) {
        await addWorkTypeMaterial(actor, estimateFeatureId, {
          customProduct: {
            productName: otherProductForm.productName,
            unitCost: otherProductForm.unitCost,
            sellPrice: otherProductForm.sellPrice,
            unitType: otherProductForm.unitType,
            quantity: otherProductForm.quantity,
          },
          quantity: otherProductForm.quantity,
          notes,
        });
      } else {
        await addWorkTypeMaterial(actor, estimateFeatureId, {
          material: selectedMaterial,
          quantity,
          notes,
        });
      }
      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Unable to add material.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <EstimateBottomSheet
      title={isEditing ? "Edit Material" : "Add Material"}
      onClose={onClose}
      onPrimary={handleSave}
      primaryLabel={isEditing ? "Save Changes" : "Add Material"}
      primaryBusy={saving}
      error={error}
    >
      {isEditing ? (
        <div className="est-row-title">{row.material_name_snapshot}</div>
      ) : (
        <MaterialSearchPicker
          materials={productsCatalog}
          value={materialId}
          onChange={setMaterialId}
          disabled={saving}
          label="Product"
          placeholder="Search Products..."
          otherOptionLabel="Other Product..."
          otherOptionValue={CATALOG_OTHER_PRODUCT}
        />
      )}

      {!isEditing && isOtherProduct && (
        <>
          <CPInput
            label="Product Name"
            value={otherProductForm.productName}
            onChange={(value) =>
              setOtherProductForm((prev) => ({ ...prev, productName: value }))
            }
            required
          />
          <CPInput
            label="Unit Cost"
            value={otherProductForm.unitCost}
            onChange={(value) =>
              setOtherProductForm((prev) => ({ ...prev, unitCost: value }))
            }
            type="number"
          />
          <CPInput
            label="Sell Price (optional)"
            value={otherProductForm.sellPrice}
            onChange={(value) =>
              setOtherProductForm((prev) => ({ ...prev, sellPrice: value }))
            }
            type="number"
          />
          <CPInput
            label="Unit Type"
            value={otherProductForm.unitType}
            onChange={(value) =>
              setOtherProductForm((prev) => ({ ...prev, unitType: value }))
            }
            placeholder="ea, bag, sqft"
          />
          <CPInput
            label="Quantity (optional)"
            value={otherProductForm.quantity}
            onChange={(value) =>
              setOtherProductForm((prev) => ({ ...prev, quantity: value }))
            }
            type="number"
          />
        </>
      )}

      {(isEditing || (!isOtherProduct && selectedMaterial)) && (
        <CPInput
          label="Quantity (optional)"
          value={quantity}
          onChange={setQuantity}
          type="number"
        />
      )}

      <CPInput label="Notes (optional)" value={notes} onChange={setNotes} />
    </EstimateBottomSheet>
  );
}
